import { Button } from "@msuite/ui/button";
import { Divider } from "@msuite/ui/divider";
import { Textarea, TextInput } from "@msuite/ui/form";
import { useLoading } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { guardNumber } from "@msuite/utils/numbers";
import type { ProtocolFormContentSectionSchema, ProtocolFormSchema } from "@msuite/utils/schemas";
import { generateId } from "@msuite/utils/strings";
import React, { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { client } from "@/hono";
import { useMentionOptions } from "@/hooks/use-mention-options";

/** Props Interface */
interface ProtocolFormContentSectionProps {
	index: number;
	section: ProtocolFormContentSectionSchema;
	isLast?: boolean;
}

export function ProtocolFormContentSection({
	index,
	section,
	isLast,
}: ProtocolFormContentSectionProps) {
	const [renderId, setRenderId] = useState(generateId());

	/** Context */
	const methods = useFormContext<ProtocolFormSchema>();
	const mentionOptions = useMentionOptions();
	const sections = useWatch({ control: methods.control, name: "content" });
	const canDelete = guardNumber(sections?.length) > 1;

	/** Hooks */
	const { t } = useTranslation();
	const asyncModal = useAsyncModal();
	const aiRefineLoader = useLoading();

	/** Functions */
	async function handleDeleteSection() {
		const isConfirmed = await asyncModal.confirm();
		if (!isConfirmed) return;
		const currentContent = methods.getValues("content") || [];
		const updatedContent = currentContent.filter((_, i) => i !== index);
		methods.setValue("content", updatedContent);
	}

	async function handleOnAIRefine() {
		aiRefineLoader.start();
		const response = await client.utils["improve-text"].$patch({
			json: {
				input: methods.getValues(`content.${index}.content`) || "",
			},
		});
		const data = await responseHandler(response);
		methods.setValue(`content.${index}.content`, data.output);
		setRenderId(generateId());
		aiRefineLoader.stop();
	}

	/** Render */
	return (
		<React.Fragment>
			<div
				className="h-stack items-start relative"
				id={section.id}
				key={renderId}
				data-cy="protocols-section"
			>
				<div className="v-stack w-full">
					<div className="h-stack">
						<TextInput
							control={methods.control}
							name={`content.${index}.title`}
							label={undefined}
							isRequired={false}
							placeholder={t("protocols.section_title_placeholder")}
							className="w-[300px] pr-0 font-medium"
							data-cy="protocols-section-title"
						/>
					</div>
					<div className="relative">
						<Textarea
							control={methods.control}
							name={`content.${index}.content`}
							label={undefined}
							isRequired={false}
							className="h-[200px]"
							data-cy="protocols-section-content"
							{...mentionOptions}
						/>
						<div className="top-1 right-1 absolute z-10 h-stack">
							<Button
								icon={<Icon name="Sparkles" />}
								size="xxs"
								variant="ghost"
								onClick={handleOnAIRefine}
								isLoading={aiRefineLoader.isLoading}
							/>
							{canDelete && (
								<Button
									icon={<Icon name="Trash" />}
									size="xxs"
									variant="danger-ghost"
									onClick={handleDeleteSection}
									data-cy="protocols-delete-section-button"
								/>
							)}
						</div>
					</div>
				</div>
			</div>
			{!isLast && (
				<Divider
					variant="content-card"
					className="my-2"
				/>
			)}
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					message={t("protocols.delete_section_confirm_message")}
				/>
			)}
		</React.Fragment>
	);
}
